import { Link } from "react-router-dom"
import {
  Calendar,
  Phone,
  MapPin,
  ChevronRight,
  MessageSquare,
  History,
  Target,
  Award,
  Users,
  Heart,
  Clock,
  Shield,
  Sparkles,
  CheckCircle,
  ArrowRight,
  Mail,
  Stethoscope,
  Zap,
  HeartPulse,
  Baby,
  Brain,
  Bone,
  User,
} from "lucide-react"
import SEO from "./SEO"

import { Button } from "../components/ui/button"
import { Card, CardContent } from "../components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs"
import { Badge } from "../components/ui/badge"

export default function AboutTest() {
     const aboutSchema = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "@id": "https://pranaam.pranaamhospitals.in/about",
    "name": "About Pranaam Hospitals",
    "url": "https://pranaam.pranaamhospitals.in/about",
    "description": "Learn about Pranaam Hospitals, a trusted multi-specialty healthcare institution in Hyderabad committed to excellence, ethics, and compassionate patient care.",
    "mainEntity": {
      "@type": "MedicalOrganization",
      "name": "Pranaam Hospitals",
      "url": "https://pranaam.pranaamhospitals.in/"
    }
  },

  {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "name": "Pranaam Hospitals",
    "url": "https://pranaam.pranaamhospitals.in/",
    "logo": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/logo.png",
    "description": "Pranaam Hospitals delivers world-class healthcare with compassion, innovation, and integrity across Hyderabad and surrounding regions.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "addressCountry": "IN"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "contactType": "emergency",
        "telephone": "+91-98485-98285",
        "availableLanguage": ["English", "Telugu", "Hindi"]
      },
      {
        "@type": "ContactPoint",
        "contactType": "appointments",
        "telephone": "+91-99660-60000",
        "availableLanguage": ["English", "Telugu", "Hindi"]
      }
    ]
  }
];
  return (
     <>
        <SEO
      title="About Pranaam Hospitals | Trusted Healthcare in Hyderabad"
      description="Learn about Pranaam Hospitals’ vision, mission, and commitment to compassionate healthcare excellence."
      keywords="About Pranaam Hospitals, multi specialty hospital Hyderabad,
                    NABH hospital Hyderabad, patient centric healthcare,
                    trusted hospital in Hyderabad, Pranaam Hospitals vision mission,
                    emergency hospital Hyderabad, advanced medical care Hyderabad"
      canonical="https://pranaam.pranaamhospitals.in/about"
      robots="index, follow"
      author="Pranaam Hospitals"
      publisher="Pranaam Hospitals"
      schema={aboutSchema}
    />
    <div className="min-h-screen bg-white">
      <main>
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="container px-4 mx-auto">
            <div className="grid items-center grid-cols-1 gap-12 md:grid-cols-2">
              <div className="max-w-lg">
                <Badge className="mb-4 px-3 py-1 bg-primary/10 text-primary border-primary/20">
                  Your Health, Our Priority Always.
                </Badge>
                <h1 className="mb-4 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
                  About <span className="text-primary">Pranaam Hospitals</span>
                </h1>
                <p className="mb-8 text-lg text-gray-700">
                  At Pranaam Hospitals, we are committed to delivering world-class healthcare with compassion,
                  innovation, and integrity. Founded with a vision to make high-quality medical services accessible and
                  affordable, Pranaam Hospitals has become a trusted name in holistic and patient-centric care.
                </p>
                <div className="flex flex-wrap gap-4">
                   <Button size="lg" asChild>
                      <a
                        href="/book-now"
                        rel="noopener noreferrer"
                      >
                        <Calendar className="mr-2 h-5 w-5" />
                        Book an Appointment
                      </a>
                    </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="gap-2 border-primary text-primary hover:bg-primary hover:text-white"
                    asChild
                  >
                    <Link to="/contact">
                      <MessageSquare className="w-5 h-5" />
                      Contact Us
                    </Link>
                  </Button>
                </div>
              </div>
              <div className="relative">
                <div className="relative h-50 overflow-hidden rounded-lg shadow-xl md:h-[385px]">
                  <img
                    src="/images/PranaamBuilding.png"
                    alt=" Building"
                    className="w-full h-full object-contain"
                  />
                  
                </div>
                {/* Material Design decorative elements */}
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-primary/10 rounded-full"></div>
                <div className="absolute -top-6 -left-6 w-24 h-24 bg-primary/10 rounded-full"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Who We Are Section */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <div className="max-w-3xl mx-auto mb-12 text-center">
              <div className="inline-flex items-center px-4 py-2 mb-4 space-x-2 text-sm font-medium rounded-full bg-primary/10 text-primary">
                <Sparkles className="w-5 h-5" />
                <span>Who We Are</span>
              </div>
              <h2 className="mb-6 text-3xl font-bold md:text-4xl">
                A Multi-Specialty Healthcare Institution
              </h2>
              <p className="text-lg text-gray-700">
                Pranaam Hospitals is equipped with cutting-edge medical technology, a highly experienced team of
                doctors, and a patient-first approach. From routine checkups to complex surgeries, we offer a
                comprehensive range of services across specialties.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              <Card className="transition-all hover:shadow-md hover:translate-y-[-5px] duration-300">
                <CardContent className="p-6 text-center">
                  <div className="flex justify-center mb-4">
                    <div className="p-3 rounded-full bg-primary/10">
                      <HeartPulse className="w-8 h-8 text-primary" />
                    </div>
                  </div>
                  <h3 className="mb-2 text-lg font-bold">Cardiology</h3>
                  <p className="text-sm text-gray-600">
                    Comprehensive heart care with advanced diagnostic and treatment options.
                  </p>
                </CardContent>
              </Card>

              <Card className="transition-all hover:shadow-md hover:translate-y-[-5px] duration-300">
                <CardContent className="p-6 text-center">
                  <div className="flex justify-center mb-4">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Bone className="w-8 h-8 text-primary" />
                    </div>
                  </div>
                  <h3 className="mb-2 text-lg font-bold">Orthopedics</h3>
                  <p className="text-sm text-gray-600">Specialized treatment for bone, joint, and muscle conditions.</p>
                </CardContent>
              </Card>

              <Card className="transition-all hover:shadow-md hover:translate-y-[-5px] duration-300">
                <CardContent className="p-6 text-center">
                  <div className="flex justify-center mb-4">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Brain className="w-8 h-8 text-primary" />
                    </div>
                  </div>
                  <h3 className="mb-2 text-lg font-bold">Neurology</h3>
                  <p className="text-sm text-gray-600">
                    Expert care for conditions affecting the brain, spine, and nervous system.
                  </p>
                </CardContent>
              </Card>

              <Card className="transition-all hover:shadow-md hover:translate-y-[-5px] duration-300">
                <CardContent className="p-6 text-center">
                  <div className="flex justify-center mb-4">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Baby className="w-8 h-8 text-primary" />
                    </div>
                  </div>
                  <h3 className="mb-2 text-lg font-bold">Pediatrics</h3>
                  <p className="text-sm text-gray-600">
                    Compassionate healthcare for infants, children, and adolescents.
                  </p>
                </CardContent>
              </Card>

              <Card className="transition-all hover:shadow-md hover:translate-y-[-5px] duration-300">
                <CardContent className="p-6 text-center">
                  <div className="flex justify-center mb-4">
                    <div className="p-3 rounded-full bg-primary/10">
                      <User className="w-8 h-8 text-primary" />
                    </div>
                  </div>
                  <h3 className="mb-2 text-lg font-bold">Gynecology & Obstetrics</h3>
                  <p className="text-sm text-gray-600">
                    Comprehensive women's health services from routine care to specialized treatments.
                  </p>
                </CardContent>
              </Card>

              <Card className="transition-all hover:shadow-md hover:translate-y-[-5px] duration-300">
                <CardContent className="p-6 text-center">
                  <div className="flex justify-center mb-4">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Stethoscope className="w-8 h-8 text-primary" />
                    </div>
                  </div>
                  <h3 className="mb-2 text-lg font-bold">General Medicine & Surgery</h3>
                  <p className="text-sm text-gray-600">
                    Comprehensive medical care and surgical procedures for various conditions.
                  </p>
                </CardContent>
              </Card>

              <Card className="transition-all hover:shadow-md hover:translate-y-[-5px] duration-300">
                <CardContent className="p-6 text-center">
                  <div className="flex justify-center mb-4">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Zap className="w-8 h-8 text-primary" />
                    </div>
                  </div>
                  <h3 className="mb-2 text-lg font-bold">Critical Care</h3>
                  <p className="text-sm text-gray-600">
                    Advanced life support and monitoring for critically ill patients.
                  </p>
                </CardContent>
              </Card>

              <Card className="transition-all hover:shadow-md hover:translate-y-[-5px] duration-300">
                <CardContent className="p-6 text-center">
                  <div className="flex justify-center mb-4">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Clock className="w-8 h-8 text-primary" />
                    </div>
                  </div>
                  <h3 className="mb-2 text-lg font-bold">Emergency Services (24/7)</h3>
                  <p className="text-sm text-gray-600">
                    Round-the-clock emergency care for immediate medical attention.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="max-w-3xl mx-auto mt-12 text-center">
              <p className="text-lg text-gray-700">
                Our focus is on early diagnosis, personalized treatment, and continuous care—ensuring the best outcomes
                for our patients.
              </p>
            </div>
          </div>
        </section>

        {/* Mission, Vision, Values Tabs */}
        <section className="py-16 bg-gray-50">
          <div className="container px-4 mx-auto">
            <div className="max-w-3xl mx-auto">
              <Tabs defaultValue="vision" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-8">
                  <TabsTrigger value="vision">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Our Vision
                  </TabsTrigger>
                  <TabsTrigger value="mission">
                    <Target className="w-4 h-4 mr-2" />
                    Our Mission
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="vision" className="p-6 border rounded-lg shadow-sm bg-white">
                  <h2 className="mb-4 text-2xl font-bold text-center">Our Vision</h2>
                  <p className="mb-6 text-center text-gray-700">
                    To be a healthcare leader known for excellence, ethics, and empathy—delivering healing that goes
                    beyond medicine.
                  </p>
                  <div className="p-4 mb-4 bg-primary/5 rounded-lg">
                    <p className="italic text-gray-700">
                      "We envision a future where every individual has access to world-class healthcare services that
                      not only treat illnesses but promote overall wellbeing and quality of life."
                    </p>
                  </div>
                </TabsContent>
                <TabsContent value="mission" className="p-6 border rounded-lg shadow-sm bg-white">
                  <h2 className="mb-4 text-2xl font-bold text-center">Our Mission</h2>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-full bg-primary/10">
                        <CheckCircle className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-gray-700">
                          To provide accessible, affordable, and advanced healthcare to every patient.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-full bg-primary/10">
                        <CheckCircle className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-gray-700">To constantly innovate and adopt the best clinical practices.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-full bg-primary/10">
                        <CheckCircle className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-gray-700">To treat every patient with dignity, respect, and care.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-full bg-primary/10">
                        <CheckCircle className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-gray-700">
                          To invest in people, technology, and infrastructure that enhance health outcomes.
                        </p>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <Card className="overflow-hidden shadow-lg">
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="relative pt-28 md:pt-0">
                  <img
                    src="/images/PranaamHospitalsAwards.png"
                    alt="Contact Pranaam Hospitals"
                    className="w-full h-full object-cover opacity-70"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-primary/40 flex items-center justify-center">
                    <div className="p-8 text-white">
                      <h3 className="mb-4 text-3xl font-bold">Get in Touch</h3>
                      <p className="mb-6">
                        Whether you're seeking a consultation, second opinion, or emergency care—we're here for you.
                      </p>
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <Phone className="w-5 h-5 mt-1 shrink-0" />
                          <div>
                            <p className="font-medium">Emergency Number</p>
                            <p>+91 98485 98285</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Phone className="w-5 h-5 mt-1 shrink-0" />
                          <div>
                            <p className="font-medium">Helpline</p>
                            <p>+91 99660 60000</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Mail className="w-5 h-5 mt-1 shrink-0" />
                          <div>
                            <p className="font-medium">Email</p>
                            <p>info@pranaamhospitals.in</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Calendar className="w-5 h-5 mt-1 shrink-0" />
                          <div>
                            <p className="font-medium">Appointments</p>
                            <p>040 230 42777 / 3777 / 35094777</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="mb-6 text-2xl font-bold">Send Us a Message</h3>
                  <form className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="name" className="block mb-2 text-sm font-medium">
                          Your Name
                        </label>
                        <input
                          type="text"
                          id="name"
                          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block mb-2 text-sm font-medium">
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="email"
                          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="subject" className="block mb-2 text-sm font-medium">
                        Subject
                      </label>
                      <input
                        type="text"
                        id="subject"
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                        placeholder="How can we help you?"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block mb-2 text-sm font-medium">
                        Message
                      </label>
                      <textarea
                        id="message"
                        rows={4}
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                        placeholder="Your message here..."
                      ></textarea>
                    </div>
                    <Button className="w-full">
                      Send Message
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </form>
                </div>
              </div>
            </Card>
          </div>
        </section>
      </main>
    </div>
    </>
  )
}